import { useState } from 'react';
import { BookOpen, Check, Plus } from 'lucide-react';
import QuestionCardEvalNat from './QuestionCardEvalNat';
import FenetreTexteSupport from './FenetreTexteSupport';
import { SUPPORTS } from '../../../generators/evalnat/formesParalleles/competences';

/**
 * La FORME PARALLÈLE de la compréhension de l'écrit dans le QCM de suivi (11/10/2026) : d'autres textes que
 * septembre, aux mêmes compétences. Un texte se prend d'un bloc, avec toutes ses questions, ou les deux textes
 * ensemble : la progression se lit par compétence et par support, et le groupe de l'outil (6e, la seule forme
 * à seuils) demande la forme complète. Les questions sont en base (communes à tous les collèges) ; leur texte,
 * ici ; leur provenance (le ministère, ou TanoEd) vient de la forme.
 */
const BoutonTexte = ({ support }) => {
  const [ouvert, setOuvert] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOuvert(true)}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5 hover:bg-blue-100 transition"
      >
        <BookOpen size={13} />
        Lire le texte
      </button>
      {ouvert ? <FenetreTexteSupport support={support} onClose={() => setOuvert(false)} /> : null}
    </>
  );
};

const FormeParalleleEvalNat = ({ forme, questionsParTexte, selectedIds, onBasculer, onPreview }) => {
  const idsDe = (texte) => (questionsParTexte.get(texte.typeLabel) || []).map((q) => q.id);
  const choisi = (ids) => ids.length > 0 && ids.every((id) => selectedIds.has(id));
  const tousLesIds = forme.textes.flatMap(idsDe);
  const toutChoisi = choisi(tousLesIds);
  const totalAttendu = forme.textes.reduce((n, t) => n + t.questions.length, 0);

  return (
    <div className="space-y-6">
      <div className="rounded-xl bg-white border border-blue-100 px-5 py-4 text-sm text-slate-700 leading-relaxed">
        <p>
          <b>Pourquoi d&apos;autres textes ?</b> En compréhension, une question n&apos;a de sens qu&apos;avec son texte : reposer
          celles de septembre mesurerait la mémoire. {forme.origine} Le résultat se lit par support et par compétence
          {forme.seuils ? ', et chaque élève reçoit un groupe s\'il passe les deux textes.' : '.'}
        </p>
        <button
          type="button"
          onClick={() => onBasculer(tousLesIds, !toutChoisi)}
          disabled={tousLesIds.length === 0}
          className={`mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${
            toutChoisi ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100' : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {toutChoisi ? <Check size={15} /> : <Plus size={15} />}
          {toutChoisi ? `Les deux textes sont ajoutés (${tousLesIds.length} questions) : retirer` : `Ajouter les deux textes (${totalAttendu} questions)`}
        </button>
      </div>

      {forme.textes.map((texte) => {
        const questions = questionsParTexte.get(texte.typeLabel) || [];
        const ids = questions.map((q) => q.id);
        const estChoisi = choisi(ids);
        const incomplet = questions.length !== texte.questions.length;
        return (
          <div key={texte.typeLabel} className={`rounded-2xl border bg-white overflow-hidden ${estChoisi ? 'border-emerald-300' : 'border-gray-200'}`}>
            <div className="px-5 py-4 flex flex-wrap items-center gap-3 border-b border-gray-100">
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{SUPPORTS[texte.support]}</p>
                <h3 className="text-[15px] font-bold text-[#0f2a4a]">« {texte.SUPPORT.titre} » · {texte.questions.length} questions</h3>
              </div>
              <BoutonTexte support={texte.SUPPORT} />
              <button
                type="button"
                onClick={() => onBasculer(ids, !estChoisi)}
                disabled={ids.length === 0}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition disabled:opacity-50 ${
                  estChoisi ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100' : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {estChoisi ? <Check size={14} /> : <Plus size={14} />}
                {estChoisi ? 'Ajouté : retirer' : 'Ajouter le texte et ses questions'}
              </button>
            </div>
            {incomplet ? (
              <p className="px-5 pt-3 text-xs text-amber-700">
                {questions.length} question{questions.length > 1 ? 's' : ''} sur {texte.questions.length} en base : ce texte n&apos;est pas complet.
              </p>
            ) : null}
            <div className="p-5 columns-1 lg:columns-2 gap-4 space-y-4">
              {questions.map((q) => (
                <div key={q.id} className="break-inside-avoid">
                  <QuestionCardEvalNat question={q} onPreview={onPreview} provenance={forme.provenance} avecTexte={false} />
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Espace pour la barre du bas */}
      <div className="h-24" />
    </div>
  );
};

export default FormeParalleleEvalNat;
